import { useEffect, useMemo } from 'react';

import CustomListView from 'components/ListView/Listview.cmp';
import { columns, defaultColDef } from './constants';
import { ListCnt } from './list.style';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import { QUERY_KEYS } from 'api/constants';
import { getGridReports } from 'api/gridReportApi';
import GridReportEmptyState from 'pages/Dashboards/EmptyState/emptyGridReportState';
import { CircularProgress } from '@mui/material';

interface IActionDropodwnProps {
  onDeleteCallback: (reportId: string) => void;
}
interface IColsProps {
  actionDropodwnProps: IActionDropodwnProps;
}

function GridReportsList({
  data = null,
  isLoading,
  setData,
  searchQuery = '',
}: {
  data?: any[] | null;
  setData?: (data: any[]) => void;
  isLoading?: boolean;
  searchQuery: string;
}) {
  const { navigateWithWorkspaceUrl } = useWorkspaceHook();

  // Fetch grid reports data only if no data is provided as props
  const gridReportsQuery = useMutation({
    mutationKey: QUERY_KEYS.GET_GRID_REPORTS,
    mutationFn: getGridReports,
  });
  useEffect(() => {
    if (!data) {
      gridReportsQuery.mutate(null, {});
    }
  }, []);

  // For delete, update the query cache
  const queryClient = useQueryClient();
  const onDeleteCallback = (reportId: string) => {
    queryClient.setQueryData([QUERY_KEYS.GET_GRID_REPORTS], (oldData: any) => {
      const filteredData = oldData?.filter((item: any) => item.id !== reportId);
      setData?.(filteredData || []);
      return filteredData || [];
    });
  };

  // Transform grid report data to match IDashboardItem format
  const transformGridReportData = (gridReports: any[]) => {
    return gridReports.map((report, index) => {
      // Get the default view or first view for display purposes
      const defaultView =
        report.GridReportViews?.find(
          (view: any) => view.id === report.defaultView,
        ) || report.GridReportViews?.[0];

      // Create a meaningful title from the report and its default view
      return {
        id: report.id,
        WorkspaceId: report.WorkspaceId,
        title: report.name || 'Performance Grid Report',
        CreatedBy: report.CreatedBy || '',
        UpdatedBy: report.UpdatedBy || '',
        createdAt: report.createdAt || null,
        updatedAt: report.updatedAt ?? null,
        deletedAt: report.deletedAt ?? null,
      };
    });
  };

  const columnProps = {
    actionDropodwnProps: { onDeleteCallback, type: 'gridReport' },
  };
  const colConfig = useMemo(() => columns(columnProps), []);

  // Transform the API response data
  const gridReportsData = data || gridReportsQuery.data || [];
  const transformedData = useMemo(
    () => transformGridReportData(gridReportsData),
    [gridReportsData],
  );

  const filteredData = transformedData.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if ((!data && gridReportsQuery.isLoading) || isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
        }}
      >
        <CircularProgress />
      </div>
    );
  }
  if (!transformedData.length) return <GridReportEmptyState />;

  return (
    <ListCnt>
      <CustomListView
        rootProps={{
          style: {
            height: 'calc(100vh - 200px)',
            boxShadow: 'none',
            border: '1px solid #d3d3d382',
            borderRadius: 8,
            overflow: 'hidden',
          },
        }}
        onRowClick={(item) => {
          navigateWithWorkspaceUrl(`/grid-report/${item}`);
        }}
        gridProps={{
          rowData: searchQuery ? filteredData : transformedData,
          ref: (ref) => {
            globalThis.gridReportsListView = ref;
          },
          onGridSizeChanged: (event) => event.api.sizeColumnsToFit(),
          headerHeight: 30,
          suppressChangeDetection: true,
          defaultColDef,
          columnDefs: colConfig,
          suppressCellFocus: true,
          getRowHeight: 100,
          suppressRowClickSelection: true,
          getRowId: (params) => params.data.id,
        }}
      />
    </ListCnt>
  );
}

export default GridReportsList;
